/**
 * The Administration endpoints, with the response shapes they answer and the guards that
 * check them.
 *
 * Shape, guard and endpoint sit together because they change together: a column added to
 * the server's response is one edit here. The request wrappers and the failure shape are
 * `request.ts`.
 */

import {
    type IAdminAccount,
    isAdminAccount,
} from '../account_models.js';
import {
    isRecord,
} from '../type_guards/values.js';
import {
    type ApiResult,
    type IReadOptions,
    requestJson,
    requestVoid,
    type VoidApiResult,
} from './request.js';

/**
 * Invites one person to the administration.
 * @param email - The address to invite.
 * @returns Whether the invitation was sent.
 */
export async function inviteAdministrator (email: string): Promise<VoidApiResult> {
    return requestVoid({
        method: 'POST',
        path: '/invitations/admin',
        body: {
            email,
        },
    });
}

/**
 * Type guard for a list of administration accounts.
 * @param value - The parsed JSON value.
 * @returns Whether the value is an array of administration accounts.
 */
function isAdminAccountList (value: unknown): value is Array<IAdminAccount> {
    return Array.isArray(value) && value.every(isAdminAccount);
}

/**
 * Lists every administration account.
 * @param options - The run's read options.
 * @returns The administration's accounts.
 */
export async function listAdministrationAccounts (
    options?: IReadOptions
): Promise<ApiResult<Array<IAdminAccount>>> {
    return requestJson({
        method: 'GET',
        path: '/admin/accounts',
        ...options,
    }, isAdminAccountList);
}

/**
 * Removes one administration account.
 * @param accountId - The UUID of the account.
 * @returns Whether the account was removed.
 */
export async function removeAdministrator (accountId: string): Promise<VoidApiResult> {
    return requestVoid({
        method: 'DELETE',
        path: `/admin/accounts/${accountId}`,
    });
}

/**
 * Reads the generated OpenAPI specification document.
 *
 * Development serves it with no session at all; every other environment gates it behind
 * `administration:manage`, mirroring the SPA route guard (`docs/csp.md` → *API
 * documentation*). The caller decides which applies, since only it knows whether a
 * session exists yet.
 * @param unauthenticated - Whether to send the request without a session.
 * @param options - The run's read options.
 * @returns The specification document.
 */
export async function getOpenApiSpec (
    unauthenticated: boolean,
    options?: IReadOptions
): Promise<ApiResult<Record<string, unknown>>> {
    return requestJson({
        method: 'GET',
        path: '/openapi.json',
        unauthenticated,
        ...options,
    }, isRecord);
}
